/**
 * Rule manager
 */
import { partition, uniq, every } from 'lodash';
import { DependencyRule } from './DependencyRule';
import { ExclusionRule } from './ExclusionRule';

export class PlanRuleManager {
  constructor() {
    this.rules = [];
  }

  // Add dependency rule (based on category)
  addDependencyRule(dependentCategory, requiredCategories, options = {}) {
    const ruleOptions = { billingCycleMatch: true, ...options };
    const rule = new DependencyRule(dependentCategory, requiredCategories, ruleOptions);
    this.rules.push(rule);
    return rule;
  }

  addDependencyRules(dependentCategorys, requiredCategories, options = {}) {
    const ruleOptions = { billingCycleMatch: true, ...options };
    const resRules = [];
    dependentCategorys.forEach((dependentCategory) => {
      const rule = new DependencyRule(dependentCategory, requiredCategories, ruleOptions);
      this.rules.push(rule);
      resRules.push(rule);
    });
    return resRules;
  }

  // Add exclusion rule (based on category or custom function)
  // @param {Array<string>|Function} categoriesOrGetConflictingPlans - Either an array of categories, or a function that receives (cart, planToAdd) and returns conflicting plans
  // @param {Object} options - Options for the rule
  addExclusionRule(categoriesOrGetConflictingPlans, options = {}) {
    const ruleOptions = {
      cascadeRemoval: true,
      sameCategoryReplacement: true,
      ...options,
    };
    const rule = new ExclusionRule(categoriesOrGetConflictingPlans, ruleOptions);
    this.rules.push(rule);
    return rule;
  }

  // Apply rules to cart
  applyRulesToCart(cart) {
    // Merge ExclusionRules before applying
    this.mergeExclusionRules();

    this.rules.forEach((rule) => cart.addRule(rule));
  }

  /**
   * Merge ExclusionRules that form complete graphs (cliques)
   * For example: if a,b are exclusive, a,c are exclusive, and b,c are exclusive,
   * they can be merged into a single rule: a,b,c are exclusive.
   * But if only a,b and a,c are exclusive (without b,c), they cannot be merged.
   */
  mergeExclusionRules() {
    // Separate ExclusionRules from other rules using lodash partition
    // Exclude rules with custom getConflictingPlans function as they cannot be merged based on categories
    const [exclusionRules, otherRules] = partition(
      this.rules,
      (rule) => rule instanceof ExclusionRule && !rule.getConflictingPlans,
    );

    if (exclusionRules.length === 0) {
      return;
    }

    // Collect all pairs as a Set of strings "cat1,cat2" (sorted)
    const pairs = new Set();
    exclusionRules.forEach((rule) => {
      const categories = rule.categories;
      for (let i = 0; i < categories.length; i++) {
        for (let j = i + 1; j < categories.length; j++) {
          pairs.add([categories[i], categories[j]].sort().join(','));
        }
      }
    });

    // Helper: check if all pairs in a group exist in pairs Set
    const hasAllPairs = (categories) => {
      return every(
        categories.flatMap((cat1, i) => categories.slice(i + 1).map((cat2) => [cat1, cat2].sort().join(','))),
        (pair) => pairs.has(pair),
      );
    };

    // Helper: check if two rules have the same options
    const hasSameOptions = (rule1, rule2) => {
      return (
        rule1.cascadeRemoval === rule2.cascadeRemoval && rule1.sameCategoryReplacement === rule2.sameCategoryReplacement
      );
    };

    // Try to merge: for each rule, try to expand it with other categories
    const mergedRules = [];
    const processed = new Set();

    exclusionRules.forEach((rule) => {
      if (processed.has(rule)) {
        return;
      }

      let mergedGroup = [...rule.categories];
      const allCategories = uniq(exclusionRules.flatMap((r) => r.categories));

      // Try to expand the group by adding categories that form complete graph
      // Only merge rules with the same options
      for (const candidate of allCategories) {
        if (mergedGroup.includes(candidate)) {
          continue;
        }
        const testGroup = [...mergedGroup, candidate];
        if (hasAllPairs(testGroup)) {
          // Check if all rules in the test group have the same options as the current rule
          const allRulesHaveSameOptions = exclusionRules
            .filter((r) => {
              const ruleCategories = r.categories;
              return (
                ruleCategories.length === 2 &&
                testGroup.includes(ruleCategories[0]) &&
                testGroup.includes(ruleCategories[1])
              );
            })
            .every((r) => hasSameOptions(r, rule));

          if (allRulesHaveSameOptions) {
            mergedGroup = testGroup;
          }
        }
      }

      // If merged group is larger, create new rule; otherwise keep original
      if (mergedGroup.length > rule.categories.length && hasAllPairs(mergedGroup)) {
        const sortedGroup = mergedGroup.sort();

        // Find all rules that are subsets of the merged group and have the same options
        const contributingRules = exclusionRules.filter(
          (r) => every(r.categories, (cat) => sortedGroup.includes(cat)) && hasSameOptions(r, rule),
        );

        // Collect unique sources from all contributing rules
        const sources = [];
        const sourceKeys = new Set();
        contributingRules.forEach((r) => {
          if (r.source) {
            const ruleSources = Array.isArray(r.source) ? r.source : [r.source];
            ruleSources.forEach((s) => {
              const key = `${s.plan}:${s.className || ''}`;
              if (!sourceKeys.has(key)) {
                sourceKeys.add(key);
                sources.push(s);
              }
            });
          }
        });

        mergedRules.push(
          new ExclusionRule(sortedGroup, {
            cascadeRemoval: rule.cascadeRemoval,
            sameCategoryReplacement: rule.sameCategoryReplacement,
            source: sources.length > 0 ? sources : undefined,
          }),
        );

        // Mark all rules that are subsets and have the same options as processed
        contributingRules.forEach((r) => {
          processed.add(r);
        });
      } else {
        mergedRules.push(rule);
        processed.add(rule);
      }
    });

    // Replace exclusion rules with merged rules
    this.rules = [...otherRules, ...mergedRules];
  }
}
